<script lang="ts">
	import FileChip from '#lib/FileChip.svelte';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import ImageLightbox from '#lib/ImageLightbox.svelte';
	import type { PendingAttachment } from '#lib/util/chat-attachments.svelte.ts';
	import { getModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import IconClose from 'virtual:icons/material-symbols/close-rounded';

	interface Props {
		items: PendingAttachment[];
		/** Shown under the chips when the files are over the per-message limits. */
		limitMessage?: string | null;
		onremove: (item: PendingAttachment) => void;
	}

	const { items, limitMessage = null, onremove }: Props = $props();

	const modalCtx = getModalContext();
</script>

<div class="attachment-chips">
	<ul>
		{#each items as item (item)}
			<li class:error={item.upload.error}>
				<!-- Images are squares of the file chips' height. One that failed shows as a file chip, so there's room for the error. -->
				{#if item.isImage && !item.upload.error}
					{@const url = item.asset?.url}
					<button
						type="button"
						class="image"
						title={item.file.name}
						disabled={!url}
						onclick={() =>
							url &&
							modalCtx.openModal(
								ImageLightbox,
								{ src: url, alt: item.file.name },
								{ appearance: 'fullscreen' }
							)}
					>
						{#if url}
							<img src={url} alt={item.file.name} />
						{:else}
							<div class="placeholder"></div>
						{/if}
					</button>
				{:else}
					<FileChip
						name={item.file.name}
						size={item.file.size}
						pageCount={item.asset?.pageCount}
						error={item.upload.error}
						uploading={item.upload.fetching}
					/>
				{/if}
				<button
					type="button"
					class="icon-button small remove"
					aria-label={t('chat.attachment.remove')}
					onclick={() => onremove(item)}
				>
					<IconClose />
				</button>
			</li>
		{/each}
	</ul>
	{#if limitMessage}
		<p class="limit">{limitMessage}</p>
	{/if}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.attachment-chips {
		@include lib.flex-column();
		gap: 0.5rem;

		ul {
			@include lib.flex-row();
			flex-wrap: wrap;
			// Room for the remove buttons, which stick out of the chips' corners.
			gap: 0.75rem;
			margin: 0;
			padding: 0;
			list-style: none;

			li {
				position: relative;

				.image {
					width: 3rem;
					min-width: unset;
					height: 3rem;
					padding: 0;
					border: 1px solid var(--border-subtle);
					border-radius: 0.5rem;
					overflow: hidden;
					background: transparent;

					img,
					.placeholder {
						width: 100%;
						height: 100%;
					}

					img {
						object-fit: cover;
					}

					.placeholder {
						@include lib.skeleton-pulse;
					}
				}

				.remove {
					position: absolute;
					top: -0.5rem;
					right: -0.5rem;
					border: 1px solid var(--border-subtle);
					background: var(--surface-1);
					font-size: 0.875rem;
					color: var(--text-secondary);

					&:hover {
						color: var(--text-primary);
					}

					// Touch screens can't hover, so they always show it.
					@media (hover: hover) {
						opacity: 0;
					}
				}

				&:hover,
				&:focus-within,
				&.error {
					.remove {
						opacity: 1;
					}
				}
			}
		}

		.limit {
			margin: 0;
			font-size: 0.875rem;
			color: var(--text-error);
		}
	}
</style>
